import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import TabNavigation from '../components/TabNavigation';
import { formatDate } from '../utils/dateUtils';
import ExcelExportModal from '../components/ExcelExportModal';
import { useAlert } from '../context/AlertContext';
import { useSite } from '../context/SiteContext';
import { usePermissions } from '../context/PermissionContext';
import { useListFilters } from '../hooks/useListFilters';

// Default filter set. Shared by the initial state, the URL/storage restore
// logic and the Reset All button so the three can never drift apart.
const DEFAULT_HAZARD_FILTERS = {
  status: 'All',
  severity: 'All',
  assignee: 'All',
  lti: 'All',
  category: 'All',
  dateFrom: '',
  dateTo: '',
  search: ''
};

const HazardsList = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const { showSuccess, showError } = useAlert();
  const { activeSiteId } = useSite();
  const { hasPermission } = usePermissions();
  const [hazards, setHazards] = useState([]);
  const [filteredHazards, setFilteredHazards] = useState([]);
  const [loading, setLoading] = useState(true);
  // Filter state is remembered across refresh and back-navigation. This page
  // filters client-side, so `filters` (live, undebounced) still drives
  // applyFilters; the hook only debounces what it mirrors to the URL.
  const {
    filters,
    setFilter: handleFilterChange,
    resetFilters,
  } = useListFilters({
    storageKey: 'hazards',
    defaults: DEFAULT_HAZARD_FILTERS,
  });
  const [aggregations, setAggregations] = useState(null);
  const [exportLoading, setExportLoading] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [csvExportLoading, setCsvExportLoading] = useState(false);

  const fetchAggregations = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (activeSiteId) params.append('site_id', activeSiteId);
      if (filters.dateFrom) params.append('date_from', filters.dateFrom);
      if (filters.dateTo) params.append('date_to', filters.dateTo);
      const qs = params.toString() ? `?${params.toString()}` : '';
      const response = await axios.get(`${API}/dashboard/safety/aggregations${qs}`);
      setAggregations(response.data);
    } catch (error) {
      console.error('Failed to fetch aggregations:', error);
    }
  }, [activeSiteId, filters.dateFrom, filters.dateTo]);

  useEffect(() => {
    fetchHazards();
    fetchAggregations();
  }, [activeSiteId]);

  useEffect(() => {
    fetchAggregations();
  }, [filters.dateFrom, filters.dateTo, fetchAggregations]);

  useEffect(() => {
    applyFilters();
  }, [hazards, filters]);

  const fetchHazards = async () => {
    try {
      console.log('Starting hazard fetch...');
      const token = localStorage.getItem('token');
      console.log('Token exists:', !!token);
      
      const params = activeSiteId ? `?site_id=${activeSiteId}` : '';
      // This page visually presents as "INCIDENTS LIST" and filters on
      // incident-shaped columns, so it reads from the new /incidents API.
      // The `/hazards` route is still reachable but the data source is the
      // post-split incidents table.
      const response = await axios.get(`${API}/incidents${params}`);
      console.log('Response received:', response);
      console.log('Hazards count:', response.data?.length);
      console.log('First hazard:', response.data?.[0]);
      
      if (Array.isArray(response.data)) {
        setHazards(response.data);
        console.log('Hazards set successfully:', response.data.length);
      } else {
        console.error('Response data is not an array:', response.data);
        setHazards([]);
      }
    } catch (error) {
      console.error('=== HAZARD FETCH ERROR ===');
      console.error('Error:', error);
      console.error('Error response:', error.response);
      console.error('Error message:', error.message);
      console.error('Error details:', error.response?.data);

      showError(`Failed to fetch hazards: ${error.response?.data?.detail || error.message}. Check browser console for details.`);
      setHazards([]);
    } finally {
      setLoading(false);
      console.log('Fetch complete, loading set to false');
    }
  };

  const applyFilters = () => {
    let filtered = [...hazards];

    if (filters.status !== 'All') {
      filtered = filtered.filter(h => h.status === filters.status);
    }
    if (filters.severity !== 'All') {
      filtered = filtered.filter(h => h.severity === filters.severity);
    }
    if (filters.assignee !== 'All') {
      filtered = filtered.filter(h => h.assignee === filters.assignee);
    }
    if (filters.lti !== 'All') {
      if (filters.lti === 'LTI Only') {
        filtered = filtered.filter(h => h.is_lti === true);
      } else if (filters.lti === 'Non-LTI') {
        filtered = filtered.filter(h => !h.is_lti);
      }
    }
    if (filters.category !== 'All') {
      filtered = filtered.filter(h => h.hazard_type === filters.category);
    }
    if (filters.dateFrom) {
      filtered = filtered.filter(h => {
        const d = h.incident_date || h.date_reported;
        return d && d.substring(0, 10) >= filters.dateFrom;
      });
    }
    if (filters.dateTo) {
      filtered = filtered.filter(h => {
        const d = h.incident_date || h.date_reported;
        return d && d.substring(0, 10) <= filters.dateTo;
      });
    }
    if (filters.search) {
      const term = filters.search.toLowerCase();
      filtered = filtered.filter(h =>
        (h.title && h.title.toLowerCase().includes(term)) ||
        (h.description && h.description.toLowerCase().includes(term)) ||
        (h.incident_number && h.incident_number.toLowerCase().includes(term)) ||
        (h.location && h.location.toLowerCase().includes(term))
      );
    }

    setFilteredHazards(filtered);
  };

  const handleExportCsv = async () => {
    setCsvExportLoading(true);
    try {
      const params = new URLSearchParams();
      if (activeSiteId) params.append('site_id', activeSiteId);
      if (filters.category !== 'All') params.append('category', filters.category);
      if (filters.status !== 'All') params.append('status', filters.status);
      if (filters.severity !== 'All') params.append('severity', filters.severity);
      if (filters.dateFrom) params.append('date_from', filters.dateFrom);
      if (filters.dateTo) params.append('date_to', filters.dateTo);
      if (filters.search) params.append('search', filters.search);
      const qs = params.toString() ? `?${params.toString()}` : '';
      const response = await axios.get(`${API}/export/csv${qs}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Saarv_Incidents_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      showSuccess('CSV file downloaded successfully!');
    } catch (error) {
      console.error('Failed to export CSV:', error);
      showError('Failed to export CSV. Please try again.');
    } finally {
      setCsvExportLoading(false);
    }
  };

  const handleExportExcel = async () => {
    setShowExportModal(true);
    setExportLoading(true);
    
    try {
      // Wait for modal animation to show
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      const excelParams = activeSiteId ? `?site_id=${activeSiteId}` : '';
      const response = await axios.get(`${API}/export/excel${excelParams}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Saarv_Incidents_Report_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      // Wait a bit more to show completion
      await new Promise(resolve => setTimeout(resolve, 1000));
      setShowExportModal(false);

      // Show success message
      setTimeout(() => {
        showSuccess('Excel file downloaded successfully!');
      }, 500);
    } catch (error) {
      console.error('Failed to export:', error);
      setShowExportModal(false);
      if (error.response?.status === 401) {
        showError('Session expired. Please login again.');
        window.location.href = '/login';
      } else {
        showError('Failed to export data. Please try again.');
      }
    } finally {
      setExportLoading(false);
    }
  };

  const getUniqueValues = (key) => {
    // The active selection is always kept selectable, even when the current
    // result set no longer contains it. Filters are remembered across visits,
    // so a restored assignee that now matches nothing would otherwise leave the
    // dropdown showing 'All' while the list stayed filtered.
    const selected = filters[key];
    const values = new Set();
    try {
      (hazards || []).forEach(h => { const v = h?.[key]; if (v) values.add(v); });
    } catch (error) {
      console.error('Error getting unique values:', error);
    }
    if (selected && selected !== 'All') values.add(selected);
    return ['All', ...values];
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'High':
        return 'bg-red-100 text-red-700';
      case 'Medium':
        return 'bg-yellow-100 text-yellow-700';
      case 'Low':
        return 'bg-green-100 text-green-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Closed':
        return 'bg-green-100 text-green-700';
      case 'Overdue':
        return 'bg-red-100 text-red-700';
      case 'In-Progress':
        return 'bg-blue-100 text-blue-700';
      case 'Open':
        return 'bg-yellow-100 text-yellow-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <TabNavigation user={user} onLogout={onLogout} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-center h-64" data-testid="loading-spinner">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-[#1B5C94]"></div>
        </div>
      </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
        <TabNavigation user={user} onLogout={onLogout} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="space-y-6" data-testid="hazards-list-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-800">INCIDENTS LIST</h1>
            <p className="text-gray-600 mt-1">
              {filteredHazards.length} incident(s) found (Total: {hazards.length})
              {' • '}
              <span className="font-semibold text-red-600">
                {hazards.filter(h => h.is_lti).length} LTI(s)
              </span>
            </p>
          </div>
          <div className="flex flex-wrap gap-3 mt-4 md:mt-0 items-center">
            {hasPermission('incidents', 'create') && (
              <button
                onClick={() => navigate('/report')}
                className="bg-[#1B5C94] text-white px-6 py-2 rounded-lg font-semibold hover:bg-[#3AC0DB] transition-colors"
                data-testid="report-incident-button"
              >
                + Report Incident
              </button>
            )}
            {hasPermission('incidents', 'export') && (
              <button
                onClick={handleExportExcel}
                disabled={exportLoading}
                className="bg-green-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-green-700 transition-colors disabled:bg-gray-400"
                data-testid="export-excel-button"
              >
                {exportLoading ? 'Exporting...' : 'Export Excel'}
              </button>
            )}
            {hasPermission('incidents', 'export') && (
              <button
                onClick={handleExportCsv}
                disabled={csvExportLoading}
                className="bg-teal-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-teal-700 transition-colors disabled:bg-gray-400"
                data-testid="export-csv-button"
              >
                {csvExportLoading ? 'Exporting...' : 'Export CSV'}
              </button>
            )}
          </div>
        </div>

        {/* Summary Cards */}
        {aggregations && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
            <div className="bg-white rounded-xl shadow-sm p-4 border-l-4 border-[#1B5C94]">
              <div className="text-2xl font-bold text-gray-800">{aggregations.total}</div>
              <div className="text-xs font-semibold text-gray-500 mt-1">Total Incidents</div>
            </div>
            <div className="bg-white rounded-xl shadow-sm p-4 border-l-4 border-red-500">
              <div className="text-2xl font-bold text-red-600">{aggregations.lti_total}</div>
              <div className="text-xs font-semibold text-gray-500 mt-1">Lost Time Injuries</div>
            </div>
            {aggregations.injury_total > 0 && (
              <div className="bg-white rounded-xl shadow-sm p-4 border-l-4 border-orange-500">
                <div className="text-2xl font-bold text-orange-600">{aggregations.injury_total}</div>
                <div className="text-xs font-semibold text-gray-500 mt-1">Total Injuries</div>
              </div>
            )}
            {aggregations.fatality_total > 0 && (
              <div className="bg-white rounded-xl shadow-sm p-4 border-l-4 border-red-700">
                <div className="text-2xl font-bold text-red-700">{aggregations.fatality_total}</div>
                <div className="text-xs font-semibold text-gray-500 mt-1">Fatalities</div>
              </div>
            )}
            {aggregations.by_category.filter(c => c.category !== 'Lost Time Injury').slice(0, 4).map((item) => (
              <div key={item.category} className="bg-white rounded-xl shadow-sm p-4 border-l-4 border-gray-300">
                <div className="text-2xl font-bold text-gray-800">{item.count}</div>
                <div className="text-xs font-semibold text-gray-500 mt-1 truncate" title={item.category}>{item.category}</div>
              </div>
            ))}
          </div>
        )}

        {/* Search Bar */}
        <div className="bg-white rounded-xl shadow-md p-4">
          <input
            type="text"
            value={filters.search}
            onChange={(e) => handleFilterChange('search', e.target.value)}
            placeholder="Search by title, description, incident number, or location..."
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5C94] text-sm"
            data-testid="search-input"
          />
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-gray-800">Filters</h2>
            <button
              onClick={resetFilters}
              className="text-sm text-[#1B5C94] hover:underline font-semibold"
            >
              Reset All
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Incident Type</label>
              <select
                value={filters.category}
                onChange={(e) => handleFilterChange('category', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5C94]"
                data-testid="category-filter"
              >
                {getUniqueValues('hazard_type').map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Status</label>
              <select
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5C94]"
                data-testid="status-filter"
              >
                {getUniqueValues('status').map(status => (
                  <option key={status} value={status}>{status}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Severity</label>
              <select
                value={filters.severity}
                onChange={(e) => handleFilterChange('severity', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5C94]"
                data-testid="severity-filter"
              >
                {getUniqueValues('severity').map(severity => (
                  <option key={severity} value={severity}>{severity}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Assignee</label>
              <select
                value={filters.assignee}
                onChange={(e) => handleFilterChange('assignee', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5C94]"
                data-testid="assignee-filter"
              >
                {getUniqueValues('assignee').map(assignee => (
                  <option key={assignee} value={assignee}>{assignee}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Lost Time Injury</label>
              <select
                value={filters.lti}
                onChange={(e) => handleFilterChange('lti', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5C94]"
                data-testid="lti-filter"
              >
                <option value="All">All Incidents</option>
                <option value="LTI Only">LTI Only</option>
                <option value="Non-LTI">Non-LTI</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Date From</label>
              <input
                type="date"
                value={filters.dateFrom}
                onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5C94]"
                data-testid="date-from-filter"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Date To</label>
              <input
                type="date"
                value={filters.dateTo}
                onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5C94]"
                data-testid="date-to-filter"
              />
            </div>
          </div>
        </div>

        {/* Incidents Table */}
        <div className="bg-white rounded-xl shadow-md overflow-hidden">
          {/* Desktop Table */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gradient-to-r from-[#1B5C94] to-[#3AC0DB] text-white">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-bold">ID</th>
                  <th className="px-6 py-4 text-left text-sm font-bold">Title</th>
                  <th className="px-6 py-4 text-left text-sm font-bold">Severity</th>
                  <th className="px-6 py-4 text-left text-sm font-bold">Status</th>
                  <th className="px-6 py-4 text-left text-sm font-bold">Assignee</th>
                  <th className="px-6 py-4 text-left text-sm font-bold">Due Date</th>
                  <th className="px-6 py-4 text-left text-sm font-bold">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredHazards.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-6 py-12 text-center text-gray-500">
                      No incidents found
                    </td>
                  </tr>
                ) : (
                  filteredHazards.map((hazard, index) => (
                    <tr key={hazard?.id || index} className="hover:bg-gray-50 transition-colors" data-testid={`hazard-row-${hazard?.id}`}>
                      <td className="px-6 py-4 text-sm text-gray-700 font-mono">{hazard?.incident_number || (index + 1)}</td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <div>
                            <div className="text-sm font-semibold text-gray-800">{hazard?.title || 'Untitled'}</div>
                            <div className="text-xs text-gray-500 mt-1">{hazard?.hazard_type || 'N/A'}</div>
                          </div>
                          {hazard?.is_lti && (
                            <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-bold bg-red-100 text-red-800 border border-red-300">
                              🚨 LTI
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-3 py-1 rounded-full text-xs font-bold ${getSeverityColor(hazard?.severity)}`}>
                          {hazard?.severity || 'N/A'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-3 py-1 rounded-full text-xs font-bold ${getStatusColor(hazard?.status)}`}>
                          {hazard?.status || 'N/A'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700">{hazard?.assignee || (hazard?.reported_by === 'anonymous' ? 'System' : hazard?.reported_by_name) || 'N/A'}</td>
                      <td className="px-6 py-4 text-sm text-gray-700">{hazard?.due_date ? formatDate(hazard.due_date) : 'N/A'}</td>
                      <td className="px-6 py-4">
                        <button
                          onClick={() => navigate(`/incidents/${hazard?.id}`)}
                          className="bg-[#1B5C94] text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-[#3AC0DB] transition-colors"
                          data-testid={`view-hazard-${hazard?.id}`}
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Mobile Cards */}
          <div className="md:hidden p-4 space-y-4">
            {filteredHazards.length === 0 ? (
              <div className="p-12 text-center text-gray-500">
                <p className="text-lg font-semibold">No incidents found</p>
                <p className="text-sm mt-2">Try adjusting your filters</p>
              </div>
            ) : (
              filteredHazards.map((hazard, index) => (
                <div key={hazard?.id || index} className="bg-gray-50 rounded-lg p-4 space-y-3">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="text-sm font-bold text-gray-500 font-mono">{hazard?.incident_number || `#${index + 1}`}</div>
                      <div className="text-lg font-semibold text-gray-800 mt-1">{hazard?.title || 'Untitled'}</div>
                      <div className="text-sm text-gray-600 mt-1">{hazard?.hazard_type || 'N/A'}</div>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <span className={`px-3 py-1 rounded-full text-xs font-bold ${getSeverityColor(hazard?.severity)}`}>
                      {hazard?.severity || 'N/A'}
                    </span>
                    <span className={`px-3 py-1 rounded-full text-xs font-bold ${getStatusColor(hazard?.status)}`}>
                      {hazard?.status || 'N/A'}
                    </span>
                    {hazard?.is_lti && (
                      <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-bold bg-red-100 text-red-800 border border-red-300">
                        🚨 LTI
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-gray-700">
                    <div><span className="font-semibold">Assignee:</span> {hazard?.assignee || (hazard?.reported_by === 'anonymous' ? 'System' : hazard?.reported_by_name) || 'N/A'}</div>
                    <div><span className="font-semibold">Due:</span> {hazard?.due_date ? formatDate(hazard.due_date) : 'N/A'}</div>
                  </div>
                  <button
                    onClick={() => navigate(`/incidents/${hazard?.id}`)}
                    className="w-full bg-[#1B5C94] text-white py-2 rounded-lg text-sm font-semibold hover:bg-[#3AC0DB] transition-colors"
                  >
                    View Details
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Excel Export Progress Modal */}
      <ExcelExportModal 
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
      />
    </div>
      </div>
  );
};

export default HazardsList;